// system.ts — system-props interpolator replicating styled-system v5 runtime
// behavior as molekule's Box used it (compose(space, color, typography,
// layout, flexbox, position, background)).
//
// Scale semantics: theme scales win when present (theme.space comes from the
// molekule defaults merged in src/theme.ts), otherwise styled-system's own
// defaults apply — matching how styled-system resolved scales at runtime:
//   space fallback:     [0, 4, 8, 16, 32, 64, 128, 256, 512]
//   fontSizes fallback: [12, 14, 16, 20, 24, 32, 48, 64, 72]
// Numeric props index the scale when in range (negatives allowed for space),
// out-of-range numbers are raw px, strings pass through.
// width: numbers in (0, 1] are fractions of 100% (styled-system layout).

const DEFAULT_SPACE = [0, 4, 8, 16, 32, 64, 128, 256, 512];
const DEFAULT_FONT_SIZES = [12, 14, 16, 20, 24, 32, 48, 64, 72];

function resolveSpace(val: any, theme: any): string {
  if (val === undefined || val === null) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'number') {
    const scale: number[] =
      theme && Array.isArray(theme.space) ? theme.space : DEFAULT_SPACE;
    const neg = val < 0;
    const abs = Math.abs(val);
    const scaled = Number.isInteger(abs) ? scale[abs] : undefined;
    if (scaled !== undefined) return `${neg ? -scaled : scaled}px`;
    return `${val}px`;
  }
  return String(val);
}

function resolveFontSize(val: any, theme: any): string {
  if (val === undefined || val === null) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'number') {
    const scale: number[] =
      theme && Array.isArray(theme.fontSizes)
        ? theme.fontSizes
        : DEFAULT_FONT_SIZES;
    const scaled = Number.isInteger(val) && val >= 0 ? scale[val] : undefined;
    if (scaled !== undefined) return `${scaled}px`;
    return `${val}px`;
  }
  return String(val);
}

// styled-system layout `width`: n in (0, 1] → percentage, else px
function resolveWidth(val: any): string {
  if (val === undefined || val === null) return '';
  if (typeof val === 'number') {
    if (val > 0 && val <= 1) return `${val * 100}%`;
    return `${val}px`;
  }
  return String(val);
}

function resolveColor(val: any, theme: any): string {
  if (val === undefined || val === null) return '';
  if (typeof val === 'string') {
    return (theme && theme.colors && theme.colors[val]) || val;
  }
  return String(val);
}

function resolvePx(val: any): string {
  if (val === undefined || val === null) return '';
  if (typeof val === 'number') return `${val}px`;
  return String(val);
}

function resolveRaw(val: any): string {
  if (val === undefined || val === null) return '';
  return String(val);
}

export function system(
  sys: Record<string, any> | undefined,
  theme: any,
): string {
  if (!sys) return '';

  const parts: string[] = [];

  const add = (prop: string, value: string) => {
    if (value !== '') parts.push(`${prop}: ${value};`);
  };

  // margin
  if (sys.m !== undefined) add('margin', resolveSpace(sys.m, theme));
  if (sys.mt !== undefined) add('margin-top', resolveSpace(sys.mt, theme));
  if (sys.mb !== undefined) add('margin-bottom', resolveSpace(sys.mb, theme));
  if (sys.ml !== undefined) add('margin-left', resolveSpace(sys.ml, theme));
  if (sys.mr !== undefined) add('margin-right', resolveSpace(sys.mr, theme));
  if (sys.mx !== undefined) {
    const v = resolveSpace(sys.mx, theme);
    add('margin-left', v);
    add('margin-right', v);
  }
  if (sys.my !== undefined) {
    const v = resolveSpace(sys.my, theme);
    add('margin-top', v);
    add('margin-bottom', v);
  }

  // padding
  if (sys.p !== undefined) add('padding', resolveSpace(sys.p, theme));
  if (sys.pt !== undefined) add('padding-top', resolveSpace(sys.pt, theme));
  if (sys.pb !== undefined) add('padding-bottom', resolveSpace(sys.pb, theme));
  if (sys.pl !== undefined) add('padding-left', resolveSpace(sys.pl, theme));
  if (sys.pr !== undefined) add('padding-right', resolveSpace(sys.pr, theme));
  if (sys.px !== undefined) {
    const v = resolveSpace(sys.px, theme);
    add('padding-left', v);
    add('padding-right', v);
  }
  if (sys.py !== undefined) {
    const v = resolveSpace(sys.py, theme);
    add('padding-top', v);
    add('padding-bottom', v);
  }

  // sizing
  if (sys.width !== undefined) add('width', resolveWidth(sys.width));
  if (sys.height !== undefined) add('height', resolvePx(sys.height));
  if (sys.maxWidth !== undefined) add('max-width', resolvePx(sys.maxWidth));
  if (sys.minWidth !== undefined) add('min-width', resolvePx(sys.minWidth));
  if (sys.minHeight !== undefined) add('min-height', resolvePx(sys.minHeight));

  // display / flex
  if (sys.display !== undefined) add('display', resolveRaw(sys.display));
  if (sys.flex !== undefined) add('flex', resolveRaw(sys.flex));
  if (sys.flexDirection !== undefined)
    add('flex-direction', resolveRaw(sys.flexDirection));
  if (sys.flexWrap !== undefined) add('flex-wrap', resolveRaw(sys.flexWrap));
  if (sys.flexGrow !== undefined) add('flex-grow', resolveRaw(sys.flexGrow));
  if (sys.flexShrink !== undefined)
    add('flex-shrink', resolveRaw(sys.flexShrink));
  if (sys.flexBasis !== undefined)
    add('flex-basis', resolveWidth(sys.flexBasis));
  if (sys.justifyContent !== undefined)
    add('justify-content', resolveRaw(sys.justifyContent));
  if (sys.justifySelf !== undefined)
    add('justify-self', resolveRaw(sys.justifySelf));
  if (sys.alignItems !== undefined)
    add('align-items', resolveRaw(sys.alignItems));
  if (sys.alignSelf !== undefined) add('align-self', resolveRaw(sys.alignSelf));
  if (sys.gap !== undefined) add('gap', resolvePx(sys.gap));
  if (sys.order !== undefined) add('order', resolveRaw(sys.order));

  // typography
  if (sys.fontSize !== undefined)
    add('font-size', resolveFontSize(sys.fontSize, theme));
  if (sys.fontWeight !== undefined)
    add('font-weight', resolveRaw(sys.fontWeight));
  if (sys.lineHeight !== undefined)
    add('line-height', resolveRaw(sys.lineHeight));
  if (sys.textAlign !== undefined) add('text-align', resolveRaw(sys.textAlign));

  // color / background
  if (sys.color !== undefined) add('color', resolveColor(sys.color, theme));
  if (sys.backgroundColor !== undefined)
    add('background-color', resolveColor(sys.backgroundColor, theme));
  if (sys.bg !== undefined)
    add('background-color', resolveColor(sys.bg, theme));

  // border
  if (sys.border !== undefined) add('border', resolveRaw(sys.border));
  if (sys.borderRadius !== undefined)
    add('border-radius', resolvePx(sys.borderRadius));
  if (sys.borderColor !== undefined)
    add('border-color', resolveColor(sys.borderColor, theme));

  // position
  if (sys.position !== undefined) add('position', resolveRaw(sys.position));
  if (sys.top !== undefined) add('top', resolvePx(sys.top));
  if (sys.right !== undefined) add('right', resolvePx(sys.right));
  if (sys.bottom !== undefined) add('bottom', resolvePx(sys.bottom));
  if (sys.left !== undefined) add('left', resolvePx(sys.left));

  // misc
  if (sys.overflow !== undefined) add('overflow', resolveRaw(sys.overflow));
  if (sys.cursor !== undefined) add('cursor', resolveRaw(sys.cursor));
  if (sys.opacity !== undefined) add('opacity', resolveRaw(sys.opacity));

  return parts.join('\n');
}

// --------------------------------------------------------------------------
// Shared prop-splitting
// --------------------------------------------------------------------------

// Canonical list of every prop key system() understands. Box-family
// components (Box, Flex, Block, Row, Column, Container, Grid, Card) accept
// the full set — molekule based them all on Box, which composed the complete
// styled-system suite. Components that intentionally accepted fewer props in
// molekule (Text, Button, Spinner, Avatar) pass their own restricted Set.
export const SYS_KEYS = [
  'm',
  'mt',
  'mb',
  'ml',
  'mr',
  'mx',
  'my',
  'p',
  'pt',
  'pb',
  'pl',
  'pr',
  'px',
  'py',
  'width',
  'height',
  'maxWidth',
  'minWidth',
  'minHeight',
  'display',
  'flex',
  'flexDirection',
  'flexWrap',
  'flexGrow',
  'flexShrink',
  'flexBasis',
  'justifyContent',
  'justifySelf',
  'alignItems',
  'alignSelf',
  'gap',
  'order',
  'fontSize',
  'fontWeight',
  'lineHeight',
  'textAlign',
  'color',
  'backgroundColor',
  'bg',
  'border',
  'borderRadius',
  'borderColor',
  'position',
  'top',
  'right',
  'bottom',
  'left',
  'overflow',
  'cursor',
  'opacity',
] as const;

export const SYS_KEYS_SET: ReadonlySet<string> = new Set(SYS_KEYS);

/**
 * Split props into the system-prop bucket (styled via system()) and the rest
 * (forwarded to the underlying element). `keys` narrows which props count as
 * system props; anything in SYS_KEYS_SET but not in `keys` is dropped
 * entirely — mirroring molekule components that ignored style props they
 * didn't compose.
 */
export function extractSysProps(
  props: Record<string, any>,
  keys: ReadonlySet<string> = SYS_KEYS_SET,
): { sys: Record<string, any>; rest: Record<string, any> } {
  const sys: Record<string, any> = {};
  const rest: Record<string, any> = {};
  for (const key of Object.keys(props)) {
    if (keys.has(key)) {
      sys[key] = props[key];
    } else if (!SYS_KEYS_SET.has(key)) {
      rest[key] = props[key];
    }
  }
  return { sys, rest };
}
